import React from "react";
import { Calendar, MessageSquare, Clock, Users, Check, AlertTriangle, RefreshCw, Sheet, Plus, Link2, Loader2 } from "lucide-react";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ColumnSelect } from "../ui/select";
import GoogleSheetsPicker from "../GoogleSheetsPicker";
import { connectGoogleSheet, createGoogleSheet, getGoogleSheetsAuthStatus, getGoogleSheetsAuthorizationUrl, getGoogleSheetTabs, SheetTab } from "@/lib/googleSheetsApi";

interface StepDataMarketingProps {
    // Campaign info
    campaignName?: string;

    // Sheet selection
    googleSheetId: string;
    googleSheetTabName: string;
    selectSheetTab: (tabName: string, tabId?: number | null) => void;
    excludedTabNames?: string[];
    excludedTabIds?: number[];
    sheetColumns: string[] | null;
    columnValidationStatus: "idle" | "loading" | "loaded" | "error";
    phoneColumn: string;
    setPhoneColumn: (value: string) => void;
    loadingAssets: boolean;
    sheets: any[];
    visibleSheets: any[];
    total: number;
    totalPages: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    start: number;
    selectSheet: (id: string) => void;
    reloadColumns?: () => Promise<void>;
    reloadSheets?: () => Promise<any[]>;

    // Meta templates
    metaTemplates: { name: string; language: string; status: string; category: string; body?: string }[];
    loadingMetaTemplates: boolean;
    selectedMetaTemplate: string;
    setSelectedMetaTemplate: (name: string) => void;

    // Scheduling
    sendDate: string;
    setSendDate: (value: string) => void;
    sendTime: string;
    setSendTime: (value: string) => void;
    sendImmediately?: boolean;
    setSendImmediately?: (value: boolean) => void;

    // Navigation
    onBack: () => void;
    onNext: () => void;
}


const EMPTY_EXCLUDED_TAB_NAMES: string[] = [];
const EMPTY_EXCLUDED_TAB_IDS: number[] = [];

const StepDataMarketing: React.FC<StepDataMarketingProps> = ({
    campaignName,
    googleSheetId, googleSheetTabName, selectSheetTab,
    excludedTabNames = EMPTY_EXCLUDED_TAB_NAMES,
    excludedTabIds = EMPTY_EXCLUDED_TAB_IDS,
    sheetColumns, columnValidationStatus, phoneColumn, setPhoneColumn,
    loadingAssets, sheets, visibleSheets, total, totalPages, currentPage, setCurrentPage, start,
    selectSheet, reloadColumns, reloadSheets,
    metaTemplates, loadingMetaTemplates, selectedMetaTemplate, setSelectedMetaTemplate,
    sendDate, setSendDate, sendTime, setSendTime, sendImmediately, setSendImmediately,
    onBack, onNext
}) => {
    const [reloadingColumns, setReloadingColumns] = React.useState(false);
    const [pickerAutoOpen, setPickerAutoOpen] = React.useState(false);
    const [showCreateSheetModal, setShowCreateSheetModal] = React.useState(false);
    const [newSheetName, setNewSheetName] = React.useState("");
    const [creatingSheet, setCreatingSheet] = React.useState(false);
    const [sheetTabs, setSheetTabs] = React.useState<SheetTab[]>([]);
    const [loadingSheetTabs, setLoadingSheetTabs] = React.useState(false);
    const currentSheetIdRef = React.useRef(googleSheetId);
    currentSheetIdRef.current = googleSheetId;

    const refreshTabsRequestRef = React.useRef(0);

    const [errors, setErrors] = React.useState<{
        sheet?: string;
        phoneColumn?: string;
        template?: string;
        sendDate?: string;
        sendTime?: string;
    }>({});

    const [showTutorialModal, setShowTutorialModal] = React.useState(false);

    React.useEffect(() => {
        if (!googleSheetId) {
            setSheetTabs([]);
            return;
        }

        let cancelled = false;

        const loadSheetTabs = async () => {
            try {
                setLoadingSheetTabs(true);

                const tabs = await getGoogleSheetTabs(googleSheetId);
                const visibleTabs = tabs.filter(
                    (tab) =>
                        !tab.hidden &&
                        !excludedTabIds.includes(tab.sheet_id) &&
                        !excludedTabNames.includes(tab.title)
                );

                if (!cancelled) {
                    setSheetTabs(visibleTabs);
                }
            } catch (error) {
                console.error("Error cargando pestañas de Google Sheets:", error);

                if (!cancelled) {
                    setSheetTabs([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingSheetTabs(false);
                }
            }
        };

        void loadSheetTabs();

        return () => {
            cancelled = true;
        };
     }, [googleSheetId, excludedTabNames, excludedTabIds]);

    
    const refreshTabsOnOpen = async () => {
        const sheetId = googleSheetId;
        if (!sheetId) return;

        const requestId = ++refreshTabsRequestRef.current;

        try {
            const tabs = await getGoogleSheetTabs(sheetId);

            if (
                currentSheetIdRef.current !== sheetId ||
                refreshTabsRequestRef.current !== requestId
            ) {
                return;
            }

            const visibleTabs = tabs.filter(
                (tab) =>
                    !tab.hidden &&
                    !excludedTabIds.includes(tab.sheet_id) &&
                    !excludedTabNames.includes(tab.title)
            );

            setSheetTabs(visibleTabs);
        } catch (error) {
            if (
                currentSheetIdRef.current !== sheetId ||
                refreshTabsRequestRef.current !== requestId
            ) {
                return;
            }

            console.error("Error actualizando pestañas:", error);
        }
    };


    React.useEffect(() => {
        // Persistencia para el tour dual. Dependencia de sheets.length, loadingAssets y googleSheetId.
        if (loadingAssets) return; // esperar a que resuelva google api auth status

        const hasSheets = sheets.length > 0;
        const flagName = hasSheets ? 'marketing_tour_stage2_seen' : 'marketing_tour_stage1_seen';

        const triggerStage4 = () => {
            return setTimeout(() => {
                import('@/components/common/MarketingCampaignTour').then(({ startMarketingSendDateTour }) => {
                    startMarketingSendDateTour(() => {
                        localStorage.setItem('marketing_tour_stage4_seen', 'true');
                    });
                }).catch(e => console.error('Error cargando Send Date Tour:', e));
            }, 300);
        };

        const triggerStage3 = () => {
            return setTimeout(() => {
                import('@/components/common/MarketingCampaignTour').then(({ startMarketingPhoneColTour }) => {
                    startMarketingPhoneColTour(() => {
                        localStorage.setItem('marketing_tour_stage3_seen', 'true');
                    });
                }).catch(e => console.error('Error cargando Phone Col Tour:', e));
            }, 300);
        };

        const triggerStage1o2 = () => {
            return setTimeout(() => {
                import('@/components/common/MarketingCampaignTour').then(({ startMarketingCampaignTour }) => {
                    startMarketingCampaignTour(hasSheets, () => {
                        localStorage.setItem(flagName, 'true');
                    });
                }).catch(e => console.error('Error cargando Marketing Tour:', e));
            }, 300);
        };

        let timeoutId: NodeJS.Timeout | undefined;

        // El usuario ya tiene hojas y seleccionó una
        if (hasSheets && googleSheetId && googleSheetTabName) {
            if (phoneColumn) {
                // Ya seleccionó hoja, pestaña y columna -> Lanzar tour stage 4
                const hasSeenStage4 = localStorage.getItem('marketing_tour_stage4_seen');
                if (!hasSeenStage4) {
                    timeoutId = triggerStage4();
                }
            } else {
                // Ya seleccionó hoja y pestaña, pero NO columna -> Lanzar tour stage 3
                const hasSeenStage3 = localStorage.getItem('marketing_tour_stage3_seen');
                if (!hasSeenStage3) {
                    timeoutId = triggerStage3();
                }
            }
        } else if (!googleSheetId) {
            const hasSeen = localStorage.getItem(flagName);
            if (!hasSeen) {
                timeoutId = triggerStage1o2();
            }
        }

        const handleRestart = (e: Event) => {
            if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
            console.log('🔄 [StepDataMarketing] Evento restart-local-tour recibido!');
            console.log('Estado actual:', { hasSheets, googleSheetId, phoneColumn });

            localStorage.removeItem('marketing_tour_stage1_seen');
            localStorage.removeItem('marketing_tour_stage2_seen');
            localStorage.removeItem('marketing_tour_stage3_seen');
            localStorage.removeItem('marketing_tour_stage4_seen');

            if (hasSheets && googleSheetId && googleSheetTabName) {
                if (phoneColumn) {
                    console.log('Lanzando stage 4');
                    timeoutId = triggerStage4();
                } else {
                    console.log('Lanzando stage 3');
                    timeoutId = triggerStage3();
                }
            } else if (!googleSheetId) {
                console.log('Lanzando stage 1o2');
                timeoutId = triggerStage1o2();
            }
            };

        window.addEventListener('restart-local-tour', handleRestart);

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            window.removeEventListener('restart-local-tour', handleRestart);
        };
    }, [sheets.length, loadingAssets, googleSheetId, googleSheetTabName, phoneColumn]);

    const handleNext = () => {
        const newErrors: typeof errors = {};

        if (!googleSheetId) {
            newErrors.sheet = "Selecciona una hoja de cálculo.";
        } else if (!googleSheetTabName) {
            newErrors.sheet = "Selecciona una pestaña de Google Sheets.";
        } else if (!sheetColumns || sheetColumns.length === 0) {
            newErrors.sheet = "La pestaña seleccionada está vacía. Añade datos a tu hoja de cálculo.";
        }

        if (googleSheetId && googleSheetTabName && sheetColumns && sheetColumns.length > 0 && !phoneColumn) {
            newErrors.phoneColumn = "Selecciona la columna de teléfono.";
        }

        // Solo validar fecha y hora si hay hojas conectadas Y NO es envío inmediato
        if (sheets.length > 0 && googleSheetId && !sendImmediately) {
            if (!sendDate) newErrors.sendDate = "Selecciona la fecha de envío.";
            if (!sendTime) newErrors.sendTime = "Selecciona la hora de envío.";

            // ✅ Validar que fecha y hora sean en el futuro (sin margen extra, el usuario decide)
            if (sendDate && sendTime) {
                const selectedDateTime = new Date(`${sendDate}T${sendTime}:00`);
                const now = new Date();
                if (selectedDateTime <= now) {
                    newErrors.sendDate = "La fecha y hora de envío deben ser en el futuro.";
                }
            }
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            onNext();
        }
    };

    const handlePickerSheetsSelected = async (
        selectedSheets: Array<{ id: string }>
    ): Promise<void> => {
        if (selectedSheets.length === 0) {
            setPickerAutoOpen(false);
            return;
        }

        try {

            for (const sheet of selectedSheets) {
                try {
                    await connectGoogleSheet(sheet.id);
                } catch (error) {
                    console.error(`Error conectando hoja ${sheet.id}:`, error);
                }
            }

            const updatedSheets = await reloadSheets?.() ?? [];
            const selectedId = selectedSheets[selectedSheets.length - 1]?.id;

            const connectedSheet = updatedSheets.find((sheet: any) =>
                String(sheet.sheet_id ?? sheet.id ?? sheet._id ?? '') === String(selectedId)
            );

            if (connectedSheet && selectedId) {
                await selectSheet(selectedId);
            }
        } catch (error) {
            console.error('Error conectando hoja desde Google Picker:', error);
            alert('No se pudo conectar la hoja seleccionada. Por favor intenta de nuevo.');
        } finally {
            setPickerAutoOpen(false);
        }
    };

    const createNewSheet = async (): Promise<void> => {
        const trimmedName = newSheetName.trim();

        if (!trimmedName) return;

        setCreatingSheet(true);

        try {

            const data = await createGoogleSheet(trimmedName);

            const newSheetId = String(
                data?.sheet_id ?? data?.id ?? ''
            );

            await reloadSheets?.();

            if (newSheetId) {
                await selectSheet(newSheetId);
                setErrors(prev => ({ ...prev, sheet: undefined }));
            }

            setNewSheetName("");
            setShowCreateSheetModal(false);
        } catch (error) {
            console.error('Error creando nueva hoja de cálculo:', error);
            alert('No se pudo crear la hoja de cálculo. Por favor intenta de nuevo.');
        } finally {
            setCreatingSheet(false);
        }
    };

    const handleConnectSheets = async (
        mode: 'selection' | 'picker',
        showErrorAlert = false
    ): Promise<void> => {
        localStorage.setItem('renewal_create_campaign_type', 'Marketing');

        if (campaignName) {
            localStorage.setItem('renewal_create_campaign_name', campaignName);
        }

        try {
            const authStatus = await getGoogleSheetsAuthStatus();

            if (authStatus.is_authenticated && !authStatus.needs_auth) {
                if (mode === 'selection') {
                    window.location.href = '/select-sheets?context=marketing-campaign';
                } else {
                    setPickerAutoOpen(true);
                }
                return;
            }

            const authorizationUrl = await getGoogleSheetsAuthorizationUrl('marketing-campaign');

            if (!authorizationUrl) {
                throw new Error('No authorization URL received');
            }

            window.location.href = authorizationUrl;
        } catch (error) {
            console.error('Error:', error);

            if (showErrorAlert) {
                alert('Error al conectar con Google Sheets. Por favor intenta de nuevo.');
            }
        }
    };

    return (
        <Card className="mt-6">
            {pickerAutoOpen && (
                <GoogleSheetsPicker
                    onSheetsSelected={handlePickerSheetsSelected}
                    onPickerClosed={() => setPickerAutoOpen(false)}
                    autoOpen={true}
                    className="hidden"
                />
            )}

            {showCreateSheetModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="mb-5">
                            <h2 className="text-xl font-bold text-gray-900">
                                Nueva Hoja de Cálculo
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Ingresa el nombre de la nueva hoja de cálculo.
                            </p>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void createNewSheet();
                            }}
                        >
                            <div className="mb-6">
                                <label
                                    htmlFor="new-sheet-name"
                                    className="mb-2 block text-sm font-medium text-gray-700"
                                >
                                    Nombre de la hoja
                                </label>

                                <input
                                    id="new-sheet-name"
                                    type="text"
                                    value={newSheetName}
                                    onChange={(e) => setNewSheetName(e.target.value)}
                                    placeholder="Ej. Clientes octubre 2026"
                                    autoFocus
                                    disabled={creatingSheet}
                                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 outline-none transition focus:border-[#1CB3B3] focus:ring-2 focus:ring-[#1CB3B3]/20 disabled:bg-gray-100"
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={creatingSheet}
                                    onClick={() => {
                                        setNewSheetName("");
                                        setShowCreateSheetModal(false);
                                    }}
                                >
                                    Cancelar
                                </Button>

                                <Button
                                    type="submit"
                                    disabled={!newSheetName.trim() || creatingSheet}
                                    className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white"
                                >
                                    {creatingSheet ? "Creando..." : "Crear Hoja"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <CardContent className="space-y-6 p-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
                        <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-lg font-semibold text-gray-900">Configurar Campaña de Marketing</div>
                        <p className="text-xs text-gray-400">Conecta tu hoja de contactos.</p>
                    </div>
                </div>

                {/* Preparar tu hoja - Guía paso a paso */}
                <div className="mt-6 relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                    <div className="flex items-start gap-5 pl-4">
                        <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">Prepara tu hoja de cálculo</h4>
                            <p className="text-xs text-gray-600 leading-relaxed mb-4">
                                Antes de continuar, asegúrate de que tu hoja tenga la columna de teléfono configurada correctamente.
                            </p>
                            <button
                                id="tour-marketing-guide"
                                onClick={() => setShowTutorialModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all animate-[pulse_2s_ease-in-out_infinite] hover:animate-none"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                Ver guía paso a paso
                            </button>
                        </div>
                    </div>
                </div>

                {/* Selecciona tu hoja de cálculo */}
                <Card className="mt-4 group relative rounded-xl border border-gray-200 bg-white p-4 mb-4 shadow-[0_8px_30px_rgba(12,20,30,0.06)]">
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                    <CardContent className="pl-4">
                        <div className="mb-3">
                            <Label className="text-lg font-semibold mb-0">Selecciona tu hoja de contactos</Label>
                            <p className="text-xs text-gray-500 mt-1">Elige la hoja de Google Sheets con los contactos a los que enviarás mensajes</p>
                        </div>
                        {errors.sheet && <p className="text-sm text-red-600 mt-2">{errors.sheet}</p>}

                        <div className="mt-2">
                            {loadingAssets ? (
                                <div className="text-xs text-gray-500 mt-1">Cargando hojas...</div>
                            ) : sheets.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-[#e6f7f4] to-[#f0f9fa] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#e0f2f1]">
                                        <Sheet className="h-8 w-8 text-[#1CB3B3]" />
                                    </div>
                                    <h3 className="font-medium mb-2">No hay hojas conectadas</h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Primero debes conectar al menos una hoja de Google Sheets para continuar con la creación de tu campaña.
                                    </p>
                                    <Button
                                        id="tour-marketing-connect"
                                        variant="outline"
                                        onClick={() => void handleConnectSheets('selection', true)}
                                        className="bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-6 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95"
                                    >
                                        Conectar Google Sheets
                                    </Button>
                                </div>
                            ) : (
                                <div>
                                    <div id="tour-marketing-sheets-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {visibleSheets.map((sheet: any, idx: number) => {
                                            const id = sheet.sheet_id ?? sheet.id ?? sheet._id ?? String(idx);
                                            const lastModified = sheet.last_modified || sheet.updated_at || sheet.created_at;
                                            const modifiedDate = lastModified ? new Date(lastModified) : null;
                                            const isSelected = String(googleSheetId) === String(id);
                                            return (
                                                <div
                                                    key={id}
                                                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all relative ${isSelected
                                                        ? "border-[#1CB3B3] bg-gradient-to-r from-[#1CB3B3]/10 to-[#179999]/10 ring-1 ring-[#1CB3B3]/20"
                                                        : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                                                        }`}
                                                    onClick={() => { selectSheet(id); setErrors(prev => ({ ...prev, sheet: undefined })); }}
                                                >
                                                    <div className="flex justify-end mb-3 sm:absolute sm:right-3 sm:top-3 sm:mb-0">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const url = sheet.url || `https://docs.google.com/spreadsheets/d/${sheet.sheet_id || sheet.id}`;
                                                                window.open(url, "_blank", "noopener,noreferrer");
                                                            }}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all"
                                                            title="Abrir hoja en Google Sheets"
                                                        >
                                                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
                                                            Abrir hoja
                                                        </button>
                                                    </div>
                                                    <h4 className="font-medium text-gray-900 mb-1 break-words sm:pr-28">
                                                    {sheet.sheet_name || sheet.name || String(id)}
                                                    </h4>
                                                    <div className="flex flex-col gap-1">
                                                        {modifiedDate && (
                                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                                <Clock className="h-3 w-3" /> Modificado: {modifiedDate.toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "numeric" })}
                                                            </p>
                                                        )}
                                                        {(sheet.row_count !== undefined && sheet.row_count > 0) && (
                                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                                <Users className="h-3 w-3" /> {sheet.row_count} contactos
                                                            </p>
                                                        )}
                                                    </div>
                                                    {isSelected && (
                                                        <div className="inline-flex items-center gap-1 mt-2 bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-xs px-2 py-1 rounded shadow-sm">
                                                            <Check className="h-3 w-3" /> Seleccionada
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {total > 6 && (
                                        <div className="flex items-center justify-between mt-4">
                                            <div className="text-sm text-gray-600">{Math.min(start + 1, total)}-{Math.min(start + visibleSheets.length, total)} de {total} hojas</div>
                                            <div className="flex items-center gap-2">
                                                <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>←</Button>
                                                <div className="px-3 py-1 rounded bg-gray-100 text-sm">{currentPage} / {totalPages}</div>
                                                <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage >= totalPages}>→</Button>
                                            </div>
                                        </div>
                                    )}

                                    {googleSheetId && (
                                        <div className="mt-4 pt-3 border-t border-gray-100">
                                            <Label className="text-base font-semibold mb-0">
                                                Selecciona la pestaña
                                            </Label>

                                            <div className="text-xs text-gray-500 mt-1 mb-2">
                                                Elige la pestaña interna de Google Sheets que contiene los contactos de esta campaña.
                                            </div>

                                            {loadingSheetTabs ? (
                                                <div className="text-xs text-gray-500">
                                                    Cargando pestañas...
                                                </div>
                                            ) : (
                                                <ColumnSelect
                                                    options={sheetTabs.map((tab) => tab.title)}
                                                    value={googleSheetTabName || ""}
                                                    onChange={(tabName) => {
                                                        const selectedName = tabName || "";
                                                        const selectedTab = sheetTabs.find(
                                                            (tab) => tab.title === selectedName
                                                        );

                                                        selectSheetTab(
                                                            selectedName,
                                                            selectedTab?.sheet_id ?? null
                                                        );

                                                        setErrors(prev => ({ ...prev, sheet: undefined }));
                                                    }}
                                                    onOpen={() => void refreshTabsOnOpen()}
                                                    placeholder="Seleccionar pestaña..."
                                                />
                                            )}

                                            {!loadingSheetTabs && sheetTabs.length === 0 && (
                                                <p className="text-xs text-amber-600 mt-2">
                                                    No se encontraron pestañas disponibles en esta hoja de cálculo.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Acciones de Google Sheets */}
                                    <div className="flex flex-col lg:flex-row justify-center gap-3 mt-6 pt-4 border-t border-gray-100">
                                        <Button
                                            onClick={() => void handleConnectSheets('picker')}
                                           className="w-full lg:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                                        >
                                            <Link2 className="w-4 h-4 mr-2" />
                                            Conectar Hojas
                                        </Button>

                                        <Button
                                            onClick={() => setShowCreateSheetModal(true)}
                                            disabled={creatingSheet}
                                            className="w-full lg:w-auto min-w-0 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 lg:px-6 min-h-11 h-auto py-2.5 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95 whitespace-normal text-center leading-tight"
                                        >
                                            <Plus className="w-4 h-4 shrink-0" />
                                            <span>Nueva Hoja de Cálculo</span>
                                        </Button>
                                    </div>

                                    {/* Column mapping - también mostrar cuando la hoja está vacía */}
                                    {googleSheetId && googleSheetTabName &&
                                        (columnValidationStatus === "loading" || columnValidationStatus === "loaded") && (
                                        <div id="tour-marketing-phone-col" className="mt-6 pt-4 border-t border-gray-100 bg-white rounded-xl p-2">
                                            <div className="mb-3">
                                                <Label className="text-lg font-semibold mb-0">Asignar columna de teléfono</Label>
                                                <div className="text-xs text-gray-500">Selecciona cuál columna contiene los números de teléfono</div>
                                            </div>

                                            {/* Mostrar advertencia si la hoja está vacía */}
                                            {columnValidationStatus === "loading" ? (
                                                <div className="flex items-center gap-2 text-gray-500 py-4">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span className="text-xs">Cargando columnas...</span>
                                                </div>
                                            ) : (!sheetColumns || sheetColumns.length === 0) ? (
                                                <div className="p-4 bg-white border border-gray-100 rounded-xl flex flex-col sm:flex-row items-start gap-3 shadow-sm">
                                                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-amber-800">La hoja está vacía</div>
                                                        <div className="text-xs text-amber-600 mt-1">
                                                            Necesitas añadir datos a tu hoja de cálculo antes de continuar.
                                                            Asegúrate de incluir una columna con los números de <strong>teléfono</strong>.
                                                        </div>
                                                       <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-sm font-medium hover:bg-[#E8F7F7] transition-all"
                                                                onClick={() => {
                                                                    const sheet = visibleSheets.find((s: any) => (s.sheet_id ?? s.id) === googleSheetId);
                                                                    const url = sheet?.url || `https://docs.google.com/spreadsheets/d/${googleSheetId}`;
                                                                    window.open(url, "_blank", "noopener,noreferrer");
                                                                }}
                                                            >
                                                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
                                                                Abrir hoja
                                                            </button>
                                                            {reloadColumns && (
                                                                <button
                                                                    type="button"
                                                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-sm font-medium hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                                                    disabled={reloadingColumns}
                                                                    onClick={async () => {
                                                                        setReloadingColumns(true);
                                                                        try {
                                                                            await reloadColumns();
                                                                        } finally {
                                                                            setReloadingColumns(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    <RefreshCw
                                                                        className={`w-4 h-4 shrink-0 ${reloadingColumns ? 'animate-spin' : ''}`}
                                                                    />
                                                                    {reloadingColumns ? 'Actualizando...' : 'Actualizar columnas'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    <div className="max-w-md">
                                                        <ColumnSelect
                                                            label="Columna teléfono"
                                                            options={sheetColumns ?? []}
                                                            value={phoneColumn}
                                                            onChange={(v: any) => { setPhoneColumn(v); setErrors(prev => ({ ...prev, phoneColumn: undefined })); }}
                                                            placeholder="Selecciona columna de teléfono"
                                                        />
                                                        {errors.phoneColumn && <p className="text-sm text-red-600 mt-2">{errors.phoneColumn}</p>}
                                                    </div>

                                                    {/* Botón para actualizar columnas */}
                                                    <div className="flex justify-center pt-2">
                                                        {reloadColumns && (
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
                                                                disabled={reloadingColumns}
                                                                onClick={async () => {
                                                                    setReloadingColumns(true);
                                                                    setPhoneColumn("");
                                                                    try {
                                                                        await reloadColumns();
                                                                    } finally {
                                                                        setReloadingColumns(false);
                                                                    }
                                                                }}
                                                            >
                                                                <RefreshCw
                                                                    className={`w-3.5 h-3.5 shrink-0 ${reloadingColumns ? 'animate-spin' : ''}`}
                                                                />
                                                                {reloadingColumns ? 'Actualizando...' : 'Actualizar columnas'}
                                                            </button>
                                                                                                                    )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Send date/time - Solo mostrar si hay hojas conectadas */}
                {sheets.length > 0 && googleSheetId && (
                    <Card id="tour-marketing-send-date" className="mt-4 group relative rounded-xl border border-gray-200 bg-white p-4 shadow-[0_8px_30px_rgba(12,20,30,0.06)]">
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                        <CardContent className="pl-4">
                            <div className="mb-3">
                                <Label className="text-lg font-semibold mb-0">Fecha y Hora de Envío</Label>
                                <p className="text-xs text-gray-500 mt-1">¿Cuándo quieres que se envíe esta campaña?</p>
                            </div>

                            {/* Checkbox de Envío Inmediato */}
                            {setSendImmediately && (
                                <label className="flex items-center gap-3 mb-4 cursor-pointer group/check">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={sendImmediately || false}
                                            onChange={(e) => {
                                                setSendImmediately(e.target.checked);
                                                if (e.target.checked) {
                                                    // Limpiar errores de fecha/hora cuando se marca envío inmediato
                                                    setErrors(prev => ({ ...prev, sendDate: undefined, sendTime: undefined }));
                                                }
                                            }}
                                            className="sr-only peer"
                                        />
                                        <div className="w-5 h-5 border-2 border-gray-300 rounded-md peer-checked:border-[#1CB3B3] peer-checked:bg-[#1CB3B3] transition-all flex items-center justify-center group-hover/check:border-[#1CB3B3]">
                                            {sendImmediately && (
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">Envío inmediato</span>
                                        <p className="text-xs text-gray-500">Enviar tan pronto como se active la campaña</p>
                                    </div>
                                </label>
                            )}

                            {/* Campos de fecha y hora - Solo mostrar si NO es envío inmediato */}
                            {!sendImmediately && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-sm">Fecha</Label>
                                        <input
                                            type="date"
                                            value={sendDate}
                                            onChange={(e) => { setSendDate(e.target.value); setErrors(prev => ({ ...prev, sendDate: undefined })); }}
                                            min={new Date().toISOString().split('T')[0]}
                                            className={`mt-2 w-full h-11 px-4 border ${errors.sendDate ? 'border-red-300' : 'border-gray-200'} rounded-xl bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/25`}
                                        />
                                        {errors.sendDate && <p className="text-sm text-red-600 mt-2">{errors.sendDate}</p>}
                                    </div>
                                    <div>
                                        <Label className="text-sm">Hora</Label>
                                        <input
                                            type="time"
                                            value={sendTime}
                                            onChange={(e) => { setSendTime(e.target.value); setErrors(prev => ({ ...prev, sendTime: undefined })); }}
                                            className={`mt-2 w-full h-11 px-4 border ${errors.sendTime ? 'border-red-300' : 'border-gray-200'} rounded-xl bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/25`}
                                        />
                                        {errors.sendTime && <p className="text-sm text-red-600 mt-2">{errors.sendTime}</p>}
                                    </div>
                                </div>
                            )}

                            {!sendImmediately && (
                                <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                                    <div className="flex flex-col sm:flex-row items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-cyan-900 mb-2">
                                                Información importante - Ley 2300 de 2023
                                            </p>
                                            <p className="text-xs text-cyan-800 mb-2">
                                                Debido a la <strong>Ley 2300 de 2023</strong> (Ley "Dejen de Fregar Colombia"),
                                                los horarios permitidos para el envío de mensajería o publicidad son:
                                            </p>
                                            <ul className="text-xs text-cyan-800 list-disc list-inside mb-2 space-y-1">
                                                <li><strong>Lunes a viernes:</strong> 7:00 AM - 7:00 PM</li>
                                                <li><strong>Sábados:</strong> 8:00 AM - 3:00 PM</li>
                                                <li><strong>Domingos y festivos:</strong> Prohibido</li>
                                            </ul>
                                            <p className="text-xs text-cyan-700 italic">
                                                No nos hacemos responsables por ningún tipo de inconveniente, multa o sanción
                                                derivada del envío de mensajes fuera de los horarios permitidos por la ley.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Mensaje informativo cuando es envío inmediato */}
                            {sendImmediately && (
                                <>
                                    <div className="p-3 bg-[#E8F7F7] border border-[#1CB3B3]/30 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1CB3B3] to-[#179999] flex items-center justify-center flex-shrink-0">
                                            <Clock className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-[#0D7377]">La campaña se enviará inmediatamente</p>
                                            <p className="text-xs text-gray-600">Los mensajes se enviarán tan pronto actives la campaña o se apruebe la plantilla</p>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                                       <div className="flex flex-col sm:flex-row items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-cyan-900 mb-2">
                                                    Información importante - Ley 2300 de 2023
                                                </p>
                                                <p className="text-xs text-cyan-800 mb-2">
                                                    Debido a la <strong>Ley 2300 de 2023</strong> (Ley "Dejen de Fregar Colombia"),
                                                    los horarios permitidos para el envío de mensajería o publicidad son:
                                                </p>
                                                <ul className="text-xs text-cyan-800 list-disc list-inside mb-2 space-y-1">
                                                    <li><strong>Lunes a viernes:</strong> 7:00 AM - 7:00 PM</li>
                                                    <li><strong>Sábados:</strong> 8:00 AM - 3:00 PM</li>
                                                    <li><strong>Domingos y festivos:</strong> Prohibido</li>
                                                </ul>
                                                <p className="text-xs text-cyan-700 italic">
                                                    No nos hacemos responsables por ningún tipo de inconveniente, multa o sanción
                                                    derivada del envío de mensajes fuera de los horarios permitidos por la ley.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}
            </CardContent>

            <CardFooter className="pt-6 border-t border-gray-100">
                <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={onBack}
                        className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm"
                    >
                        ← Atrás
                    </Button>
                    <Button
                        onClick={handleNext}
                        className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95"
                    >
                        Siguiente →
                    </Button>
                </div>
            </CardFooter>

            {/* Tutorial Modal */}
            {showTutorialModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setShowTutorialModal(false)}>
                    <div className="relative bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowTutorialModal(false)} aria-label="Cerrar guía" className="absolute top-3 right-3 z-20 bg-white/80 hover:bg-white rounded-full p-1 focus:outline-none">
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <div className="flex items-center justify-center" style={{ padding: 12 }}>
                            <img
                                src="/assets/marketing.png"
                                alt="Guía paso a paso - Marketing"
                                className="rounded-md shadow-md"
                                style={{ width: '100%', height: 'auto', maxHeight: 'calc(95vh - 24px)', objectFit: 'contain' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default StepDataMarketing;
